import { CodeHighlightTabs } from "@mantine/code-highlight";
import {
    ActionIcon,
    Anchor,
    Badge,
    BadgeProps,
    Button,
    Code,
    Divider,
    Group,
    Paper,
    Skeleton,
    Stack,
    Table,
    Tabs,
    Text,
} from "@mantine/core";
import { IconBrandHtml5, IconFileText, IconMarkdown, IconReload } from "@tabler/icons-react";
import { queryOptions, useSuspenseInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { PropertyBadge } from "shared/components/global/PropertyBadge";
import { InsightsChart } from "shared/components/message/insights/chart";
import { getRelativeTime, RelativeTime } from "shared/components/RelativeTime";
import { Shiki } from "shared/components/shiki";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { usePromise } from "shared/hooks/usePromise";
import { useAside } from "shared/utils/aside";
import { to, voiding } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { mdToHtml } from "shared/utils/md";
import { qk } from "shared/utils/qk";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { graphql } from "src/gql";
import { MessageStatus } from "src/gql/graphql";
import { match, P } from "ts-pattern";
import z from "zod";

const Page = () => {
    const { data, refetch, isRefetching, dataUpdatedAt } = useSuspenseInfiniteQuery(queries.inboxTable);
    const aside = useAside();
    const inbox = data.pages.flat();

    return (
        <Content paper>
            <Content.Heading backable>Message-o-Matic™</Content.Heading>
            <Content.Action>
                <Group ml="md">
                    <Text size="xs" c="dimmed" key={Date.now()}>
                        Updated at {new Date(dataUpdatedAt).toLocaleTimeString()}
                    </Text>
                    <ActionIcon variant="default" onClick={voiding(refetch)} loading={isRefetching}>
                        <IconReload size={16} />
                    </ActionIcon>
                </Group>
            </Content.Action>

            <Table.ScrollContainer minWidth={"100%"}>
                <Table striped withRowBorders styles={{ td: { padding: "2px 4px" } }}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Subject</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Sender</Table.Th>
                            <Table.Th>
                                <abbr title="Recipient Metric Count">RMC.</abbr>
                            </Table.Th>
                            <Table.Th>
                                <abbr title="Published At">Pub.</abbr>
                            </Table.Th>
                            <Table.Th>Updated</Table.Th>
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {inbox.map((each) => (
                            <Table.Tr key={each.id + dataUpdatedAt}>
                                <Table.Td>
                                    <Text size="xs" lineClamp={1} truncate="end" title={each.subject}>
                                        {each.subject}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <AbbrTag
                                        color={
                                            (
                                                {
                                                    [MessageStatus.Published]: "green",
                                                    [MessageStatus.Scheduled]: "indigo",
                                                } as Record<MessageStatus, string | undefined>
                                            )[each.status] ?? "gray"
                                        }
                                    >
                                        {each.status}
                                    </AbbrTag>
                                </Table.Td>

                                <Table.Td>
                                    <Anchor size="xs" td="underline" href={`/organization/groups/${each.sender.group.id}`}>
                                        {each.sender.name}
                                    </Anchor>
                                </Table.Td>

                                <Table.Td>{each.insights.metricsCount}</Table.Td>
                                <Table.Td>
                                    {!!each.publishedAt ? (
                                        <Text inline size="xs">
                                            <RelativeTime withTime date={+each.publishedAt}></RelativeTime>
                                        </Text>
                                    ) : (
                                        <AbbrTag>N/A</AbbrTag>
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    <Text inline size="xs">
                                        <RelativeTime withTime date={+each.updatedAt}></RelativeTime>
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <Button
                                        size="compact-xs"
                                        variant="transparent"
                                        td="underline"
                                        onClick={() => {
                                            aside.present({
                                                label: each.subject,
                                                component: (
                                                    <Shiki.Provider>
                                                        <Suspense fallback={<Skeleton h="400px" w="400px" />}>
                                                            <MessagePanel messageId={each.id} />
                                                        </Suspense>
                                                    </Shiki.Provider>
                                                ),
                                                mode: "drawer",
                                            });
                                        }}
                                    >
                                        More
                                    </Button>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
                <LoadMoreTrigger query={queries.inboxTable} />
            </Table.ScrollContainer>
        </Content>
    );
};

const AbbrTag = ({ children, label, ...props }: BadgeProps & { label?: string }) => (
    <Badge size="xs" {...props}>
        {typeof children == "string" ? <abbr title={label ?? children}>{children.slice(0, 3)}</abbr> : children}
    </Badge>
);

const MessagePanel = (props: { messageId: string }) => {
    const isMobile = useIsMobile();
    const { data } = useSuspenseQuery(queries.message(props.messageId));
    const localesHtml = usePromise(
        () => Promise.all(data.locales.map(async (each) => ({ ...each, html: await mdToHtml(each.richBody) }))),
        [data],
    );

    return (
        <Stack maw={!isMobile ? "50vw" : undefined}>
            <Table withTableBorder variant="vertical" layout="fixed" w="50vw">
                <Table.Tbody>
                    <Table.Tr>
                        <Table.Th w={160}>UUID</Table.Th>
                        <Table.Td>
                            <Code>{data.id}</Code>
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Status</Table.Th>
                        <Table.Td>
                            <Badge variant="default">{data.status}</Badge>
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Severity</Table.Th>
                        <Table.Td>
                            <Badge variant="default">{data.severity}</Badge>
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Scheduled For</Table.Th>
                        <Table.Td>
                            {!!data.scheduledFor ? (
                                <Badge variant="default">{new Date(data.scheduledFor).toLocaleDateString()}</Badge>
                            ) : (
                                "N/A"
                            )}
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Duration</Table.Th>
                        <Table.Td>
                            {match(data.duration)
                                .with({ span: P.number }, ({ span }) => <PropertyBadge title="Minutes">{span}</PropertyBadge>)
                                .with({ absolute: P.string }, ({ absolute }) => (
                                    <PropertyBadge title="absolute">{z.coerce.date().parse(+absolute).toLocaleString()}</PropertyBadge>
                                ))
                                .otherwise(() => "None")}
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Published At</Table.Th>
                        <Table.Td>
                            <Text size="sm">
                                {maybe(data.publishedAt)?.take(
                                    (it) => `${z.coerce.date().parse(+it).toLocaleString()} (${getRelativeTime(+it)})`,
                                ) ?? "N/A"}
                            </Text>
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Expired At</Table.Th>
                        <Table.Td>
                            <Text size="sm">
                                {maybe(data.expireAt)?.take(
                                    (it) => `${z.coerce.date().parse(+it).toLocaleString()} (${getRelativeTime(+it)})`,
                                ) ?? "N/A"}
                            </Text>
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Updated At</Table.Th>
                        <Table.Td>
                            <Text size="sm">
                                {maybe(data.updatedAt)?.take(
                                    (it) => `${z.coerce.date().parse(+it).toLocaleString()} (${getRelativeTime(+it)})`,
                                ) ?? "N/A"}
                            </Text>
                        </Table.Td>
                    </Table.Tr>
                </Table.Tbody>
            </Table>

            <Text size="sm" fw="bold">
                Audience
            </Text>
            <Table variant="vertical" layout="fixed" withTableBorder w="50vw">
                <Table.Tbody>
                    <Table.Tr>
                        <Table.Th w={160}>Groups</Table.Th>
                        <Table.Td>
                            {maybe(
                                data.audience.groups.map((grp) => (
                                    <Badge key={grp.id} variant="default" size="sm">
                                        {grp.name}
                                    </Badge>
                                )),
                            ).takeIf((it) => it.length > 0) ?? "None"}
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th w={160}>Topics</Table.Th>
                        <Table.Td>
                            {maybe(
                                data.audience.topics.map((tpc) => (
                                    <Badge key={tpc.id} variant="default" size="sm">
                                        {tpc.name}
                                    </Badge>
                                )),
                            ).takeIf((it) => it.length > 0) ?? "None"}
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th w={160}>Users</Table.Th>
                        <Table.Td>
                            {maybe(
                                data.audience.users.map((usr) => (
                                    <Badge key={usr.id} variant="default" size="sm">
                                        {usr.loginEmail}
                                    </Badge>
                                )),
                            ).takeIf((it) => it.length > 0) ?? "None"}
                        </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th w={160}>GeoFilters</Table.Th>
                        <Table.Td>
                            {maybe(
                                data.audience.geofilters.map((gf) => (
                                    <Badge key={gf.id} variant="default" size="sm">
                                        {gf.name}
                                    </Badge>
                                )),
                            ).takeIf((it) => it.length > 0) ?? "None"}
                        </Table.Td>
                    </Table.Tr>
                </Table.Tbody>
            </Table>

            <Text size="sm" fw="bold">
                Escalations
            </Text>

            <Table variant="vertical" layout="fixed" withTableBorder w="50vw">
                <Table.Tbody>
                    {data.escalations
                        .toSorted((it) => it.priority)
                        .map((esc, i) => (
                            <Table.Tr key={esc.id}>
                                <Table.Th w={160}>Escalation {i + 1}</Table.Th>
                                <Table.Td>After {esc.delaySec / 60} minutes</Table.Td>
                            </Table.Tr>
                        ))}
                    <Table.Tr>
                        <Table.Th w={160}>Acknowledgable</Table.Th>
                        <Table.Td>
                            <Badge variant="light" size="sm" color={data.acknowledgable ? "green" : "red"}>
                                {data.acknowledgable ? "Yes" : "No"}
                            </Badge>
                        </Table.Td>
                    </Table.Tr>
                </Table.Tbody>
            </Table>

            <Divider />

            <Tabs defaultValue="RichBody">
                <Tabs.List>
                    <Tabs.Tab value="RichBody" leftSection={<IconMarkdown size={16} />}>
                        Rich Body
                    </Tabs.Tab>
                    <Tabs.Tab value="PlainBody" leftSection={<IconFileText size={16} />}>
                        Plain Body
                    </Tabs.Tab>
                    <Tabs.Tab value="HTML" leftSection={<IconBrandHtml5 size={16} />}>
                        Generated HTML
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="RichBody">
                    <CodeHighlightTabs
                        radius="md"
                        code={data.locales.map((locale) => ({
                            fileName: locale.language,
                            language: "markdown",
                            code: locale.richBody,
                        }))}
                    />
                </Tabs.Panel>

                <Tabs.Panel value="PlainBody">
                    <CodeHighlightTabs
                        radius="md"
                        code={data.locales.map((locale) => ({ fileName: locale.language, code: locale.body }))}
                    />
                </Tabs.Panel>

                <Tabs.Panel value="HTML">
                    <CodeHighlightTabs
                        radius="md"
                        code={localesHtml.map((locale) => ({ fileName: locale.language, code: locale.html, language: "html" }))}
                    />
                </Tabs.Panel>
            </Tabs>

            <Group justify="space-between" align="flex-start">
                <Paper flex={1} p="sm">
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Quartz Job</Table.Th>
                                <Table.Th>Scheduled For</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        {Object.entries(data.jobs).map(([job, scheduledFor]) => (
                            <Table.Tr key={job}>
                                <Table.Td>
                                    <Badge variant="default" size="sm">
                                        {job}
                                    </Badge>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm">
                                        {maybe(scheduledFor)?.take(
                                            (it) => `${z.coerce.date().parse(+it).toLocaleString()} (${getRelativeTime(+it)})`,
                                        ) ?? "N/A"}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table>
                </Paper>
                <Paper p="sm">
                    <Stack>
                        <Text size="sm" fw="bold">
                            Delivery Metrics
                        </Text>

                        <InsightsChart msgId={data.id} />
                    </Stack>
                </Paper>
            </Group>
        </Stack>
    );
};

const queries = {
    inboxTable: createOffsetBasedGqlQueryOptions({
        document: graphql(`
            query DevtoolsMessage_O_MaticTable($limit: Int!, $offset: Int!) {
                inbox(limit: $limit, offset: $offset) {
                    id
                    subject
                    status
                    acknowledgable
                    severity
                    sender {
                        name
                        group {
                            id
                            name
                        }
                    }
                    insights {
                        metricsCount
                    }
                    updatedAt
                    publishedAt
                    jobs {
                        expire
                        publish
                    }
                }
            }
        `),
        queryKey: qk("messages", "devtools", "inbox"),
        select: to("inbox"),
    }),
    message: (id: string) =>
        queryOptions({
            queryKey: qk("messages", "devtools", "id", id),
            select: to("message"),
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query DevtoolsMessage_O_MaticMessageDetails($id: UUID!) {
                            message(id: $id) {
                                id
                                subject
                                status
                                severity
                                acknowledgable
                                scheduledFor
                                audience {
                                    groups {
                                        id
                                        name
                                    }
                                    topics {
                                        id
                                        name
                                    }
                                    users {
                                        id
                                        loginEmail
                                    }
                                    geofilters {
                                        id
                                        name
                                    }
                                }
                                duration {
                                    absolute
                                    span
                                }
                                expireAt
                                escalations {
                                    id
                                    priority
                                    delaySec
                                }
                                files {
                                    id
                                    name
                                    type
                                }
                                sender {
                                    id
                                    name
                                    group {
                                        id
                                        name
                                    }
                                }
                                locales {
                                    language
                                    body
                                    richBody
                                    subject
                                    customizations {
                                        enabled
                                        value
                                    }
                                }
                                jobs {
                                    expire
                                    publish
                                }
                                options {
                                    id
                                    method
                                }
                                notes {
                                    id
                                    subject
                                }
                                publishedAt
                                publishedBy {
                                    id
                                    loginEmail
                                    name
                                }
                                updatedAt
                            }
                        }
                    `),
                    { id },
                ),
        }),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/message-o-matic")({
    component: Page,
});
